import { createGtm } from '@gtm-support/vue-gtm'
import { useNavigationStore } from '~/store/navigation'
import { createFullUrl } from '@/composables/util'

export default defineNuxtPlugin((nuxtApp) => {
  const config = useRuntimeConfig()
  const url = useRequestURL()
  const router = useRouter()
  const navigationStore = useNavigationStore()

  /*
   * Local dataLayer debugging: set `NUXT_PUBLIC_GTM_DEV=true` in your `nuxt/.env` and restart the dev
   * server. This switches the integration on so events build `window.dataLayer` up in the browser and
   * each dispatch is logged, while `loadScript` stays off, so the container is never fetched and nothing
   * reaches Google. Tracking gates on `enabled` alone, never on `loadScript`, which is what makes the
   * combination safe.
   */
  const isGtmDevMode = import.meta.dev && config.public.gtmDev

  const head = injectHead()

  /**
   * Resolves with the document title once Unhead has written it to the DOM.
   *
   * Nuxt suspends head updates for the duration of a navigation: its head plugin sets `pauseDOMUpdates`
   * on `page:start`, suppresses `dom:beforeRender` while it is set, and calls `syncHead()` on
   * `page:finish`. `router.afterEach`, where this event is tracked, runs before all of that, so reading
   * the title there reports the page the visitor just left. `page:finish` alone is not enough either,
   * because Nuxt does not await `syncHead()` and `renderDOMHead` schedules the write. `dom:rendered` is
   * the point the title is actually in the document.
   *
   * The timeout is a safety net for a navigation that never renders a head. It resolves with whatever the
   * title is then rather than rejecting: a wrong title costs less than a page view that never arrives.
   */
  const titleAfterHeadFlush = (timeoutMs = 2000) =>
    new Promise((resolve) => {
      const settle = () => {
        clearTimeout(timer)
        unhook?.()
        resolve(document.title)
      }

      const timer = setTimeout(settle, timeoutMs)
      const unhook = head?.hooks?.hook('dom:rendered', settle)

      if (!unhook) {
        settle()
      }
    })

  nuxtApp.vueApp.use(
    createGtm({
      debug: isGtmDevMode,
      defer: true,
      enabled: !import.meta.dev || isGtmDevMode,
      id: config.public[url.hostname.includes('inspectiedienst') ? 'lidGoogleTagManagerId' : 'dbGoogleTagManagerId'],
      loadScript: !import.meta.dev,
      vueRouter: router,
      // Mimic the behavior of the Nuxt 2 module
      trackViewEventProperty: 'nuxtRoute',
      vueRouterAdditionalEventData: async (to) => {
        /*
         * The referrer is read first: `1.route.client.ts` updates it in an earlier `afterEach`, so it is
         * already correct for this navigation, and reading it after the wait below would pick up the next
         * one if the visitor clicks again within it.
         */
        const currentFullUrl = createFullUrl(to, config)
        const referrer = navigationStore.referrer && navigationStore.referrer !== currentFullUrl ? navigationStore.referrer : document.referrer || ''

        // vue-gtm awaits this callback before pushing, so the wait for the title belongs inside it.
        const pageTitle = await titleAfterHeadFlush()

        return {
          routeName: to.name,
          pageType: 'PageView',
          pageUrl: to.fullPath,
          pageTitle,
          referrer,
        }
      },
    }),
  )
})
